import type { Scene } from '@babylonjs/core/scene';
import { TargetCamera } from '@babylonjs/core/Cameras/targetCamera';
import { Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { CameraConfig } from '../config/camera.config';
import type { TrackFrame } from '../level/TrackManager';
import { forwardOf, rightOf } from '../level/connectors/Pose';
import { clamp, damp, dampAngle, DEG_TO_RAD, lerp, smoothDamp, type SmoothDampState } from '../utils/math';
import type { PlayerSnapshot } from '../player/PlayerController';
import {
  DriveCameraModifier,
  LandingDipModifier,
  ShakeModifier,
  ShowcaseModifier,
  SlideCameraModifier,
  SpeedFovModifier,
  TurnAnticipationModifier,
  type CameraAdjustments,
  type CameraModifier,
} from './CameraModifiers';
import { PivotContinuity, type PlanarPoint } from './PivotContinuity';

/** Width of the soft transition between free camera height and an overhead limit (meters). */
const OVERHEAD_BLEND = 0.15;

/** Smooth minimum: like Math.min, but without a kink where the two values cross. */
function softMin(a: number, b: number, k: number): number {
  const m = Math.min(a, b);
  return m - k * Math.log(Math.exp((m - a) / k) + Math.exp((m - b) / k));
}

export interface CameraFollowInput {
  readonly snapshot: PlayerSnapshot;
  /** Track frame at the player's interpolated distance. */
  readonly frame: TrackFrame;
  readonly normalizedSpeed: number;
  readonly time: number;
  readonly sliding: boolean;
  /** 0 on the open site … 1 inside an enclosure at the camera (see CameraModifierContext). */
  readonly enclosed?: number;
  /**
   * Lowest underside of overhead obstacles between two track distances around a
   * lateral position, or null. Keeps the camera from cutting through scaffolds.
   */
  readonly ceilingAt?: (fromDistance: number, toDistance: number, lateral: number) => number | null;
  /**
   * Highest walkable ground (container roofs, platforms) between two track distances around a
   * lateral position, or null over a pit. The camera never dips into it.
   */
  readonly groundAt?: (fromDistance: number, toDistance: number, lateral: number) => number | null;
  /**
   * The runner's bend around a junction corner (PlayerView.bend): world offset from the
   * track path and heading. The rig follows the body through the turn, not the path's kink.
   */
  readonly bend?: Readonly<{ offsetX: number; offsetZ: number; yaw: number }> | null;
  /** The runner rides a vehicle (dozer set piece): higher and further back (DriveCameraModifier). */
  readonly driving?: boolean;
  /** The vehicle is the ructec-Bulli, and its speed share 0..1. */
  readonly drivingBus?: boolean;
  readonly busSpeedShare?: number;
  /**
   * Height the runner stands on (PlayerController.standingLevel): followed fully, only the jump above it by
   * verticalFollowFactor – on the shell's upper floor the camera sits over the runner exactly as on the road.
   */
  readonly standingLevel?: number;
}

/** Camera height target: the standing level fully, the jump above it partially. */
const followHeight = (input: CameraFollowInput, factor: number): number => {
  const level = input.standingLevel ?? 0;
  return level + (input.snapshot.y - level) * factor;
};

/**
 * Third-person follow camera in track space: lateral/vertical/forward
 * damping are separate so lane changes feel smooth while forward motion
 * stays tight. Effects are stacked via CameraModifiers.
 */
export class ThirdPersonCamera {
  readonly camera: TargetCamera;
  readonly shake: ShakeModifier;
  readonly landing: LandingDipModifier;
  readonly turnAnticipation: TurnAnticipationModifier;
  /** Orbit for the extra-life drink. */
  readonly showcase = new ShowcaseModifier();

  private readonly modifiers: CameraModifier[];
  private readonly adjustments: CameraAdjustments = {
    offsetX: 0,
    offsetY: 0,
    offsetZ: 0,
    targetOffsetY: 0,
    rollDegrees: 0,
    fovDegrees: 60,
    yawOffsetDegrees: 0,
    focusRunner: 0,
  };
  private lateral = 0;
  private height = 0;
  private lag = 0;
  private yaw = 0;
  private readonly target = new Vector3();
  private readonly viewDirection = new Vector3();
  private readonly rollRotation = new Quaternion();
  /** Height limit under overhead obstacles (relative to the ground), glides by spring. */
  private ceilingLimit = Infinity;
  private readonly ceilingSpring: SmoothDampState = { velocity: 0 };
  /** Lowest camera height over raised ground along the path (relative to the track), glides by spring. */
  private floorLimit = -Infinity;
  private readonly floorSpring: SmoothDampState = { velocity: 0 };
  private readonly pivotContinuity: PivotContinuity;
  private readonly rawPivot: PlanarPoint = { x: 0, z: 0 };
  private readonly runner: PlanarPoint = { x: 0, z: 0 };
  private readonly pivot: PlanarPoint = { x: 0, z: 0 };

  constructor(
    scene: Scene,
    private readonly config: CameraConfig,
  ) {
    this.camera = new TargetCamera('CAMERA_MAIN', new Vector3(0, config.height, -config.followDistance), scene);
    this.camera.minZ = config.nearPlane;
    this.camera.maxZ = config.farPlane;
    this.camera.fov = config.fov.baseDegrees * DEG_TO_RAD;
    this.shake = new ShakeModifier(config.shake);
    this.landing = new LandingDipModifier(config.landing);
    this.turnAnticipation = new TurnAnticipationModifier(config.turnAnticipation);
    this.pivotContinuity = new PivotContinuity(config.turnFollow);
    this.modifiers = [
      new SpeedFovModifier(config.fov),
      new SlideCameraModifier(config.slide),
      new DriveCameraModifier(config.drive),
      this.shake,
      this.landing,
      this.turnAnticipation,
      this.showcase,
    ];
  }

  /** Jumps to the target without damping (run start / restart). */
  snap(input: CameraFollowInput): void {
    this.lateral = input.snapshot.x * this.config.lateralFollowFactor;
    this.height = followHeight(input, this.config.verticalFollowFactor);
    this.lag = 0;
    this.yaw = input.bend?.yaw ?? input.frame.yaw;
    this.ceilingLimit = Infinity;
    this.ceilingSpring.velocity = 0;
    this.floorLimit = -Infinity;
    this.floorSpring.velocity = 0;
    this.pivotContinuity.reset();
    for (const modifier of this.modifiers) modifier.reset();
    this.update(0, input);
  }

  update(dt: number, input: CameraFollowInput): void {
    const c = this.config;
    const { snapshot, frame } = input;
    const portrait = this.isPortrait();

    this.lateral = damp(this.lateral, snapshot.x * c.lateralFollowFactor, portrait ? c.portrait.lateralDamping : c.positionDamping.lateral, dt);
    this.height = damp(this.height, followHeight(input, c.verticalFollowFactor), c.positionDamping.vertical, dt);
    // Forward lag grows slightly with speed changes and settles quickly – a subtle sense of acceleration.
    this.lag = damp(this.lag, 0, c.positionDamping.forward, dt);
    // Through a turn the rig follows the body's bend heading (continuous) instead of the path's 90° kink.
    this.yaw = dampAngle(this.yaw, input.bend?.yaw ?? frame.yaw, c.rotationDamping, dt);

    const a = this.adjustments;
    a.offsetX = a.offsetY = a.offsetZ = a.targetOffsetY = a.rollDegrees = a.yawOffsetDegrees = a.focusRunner = 0;
    a.fovDegrees = c.fov.baseDegrees;
    const ctx = { normalizedSpeed: input.normalizedSpeed, time: input.time, sliding: input.sliding, enclosed: input.enclosed ?? 0, driving: input.driving ?? false, drivingBus: input.drivingBus ?? false, busSpeedShare: input.busSpeedShare ?? 0 };
    for (const modifier of this.modifiers) modifier.update(dt, ctx, a);

    const yaw = this.yaw + a.yawOffsetDegrees * DEG_TO_RAD;
    const fwd = forwardOf(yaw);
    const right = rightOf(yaw);
    // Pivot = player's lateral position (partially followed) on the centerline frame – measured
    // along the frame's own right (the rig's damped yaw still points down the old leg right after a
    // turn), plus the body's bend. Kept continuous where the frame jumps at a junction turn.
    // Focused on the runner the pivot sits exactly on its lane, measured along the track: a large orbit yaw
    // (showcase) must not swing the lane offset to the other side of the road.
    const focus = clamp(a.focusRunner, 0, 1);
    const lateralRight = rightOf(frame.yaw + a.yawOffsetDegrees * DEG_TO_RAD * (1 - focus));
    const trackRight = rightOf(frame.yaw);
    const lateral = lerp(this.lateral, snapshot.x, focus);
    this.rawPivot.x = frame.x + lateralRight.x * lateral + (input.bend?.offsetX ?? 0);
    this.rawPivot.z = frame.z + lateralRight.z * lateral + (input.bend?.offsetZ ?? 0);
    this.runner.x = frame.x + trackRight.x * snapshot.x;
    this.runner.z = frame.z + trackRight.z * snapshot.x;
    const { x: px, z: pz } = this.pivotContinuity.apply(dt, this.rawPivot, this.runner, frame.yaw, this.pivot);
    const back = (portrait ? c.portrait.followDistance : c.followDistance) + this.lag - a.offsetZ;

    // Overhead obstacles: look ahead along the camera path and glide down in time
    // (and back up afterwards) – a hard clamp makes the camera jump.
    const desiredY = this.height + (portrait ? c.portrait.height : c.height) + a.offsetY;
    const cameraDistance = snapshot.distance - back;
    const ceiling = input.ceilingAt?.(cameraDistance - 0.5, cameraDistance + c.overheadLookAhead, this.lateral + a.offsetX) ?? null;
    // The limit glides on its own spring (independent of slide/jump moves) and is blended in
    // with a soft minimum – no kink when the camera starts or stops following the limit.
    const freeLimit = desiredY + 2 * OVERHEAD_BLEND;
    const targetLimit = ceiling === null ? freeLimit : Math.min(freeLimit, ceiling - c.overheadClearance);
    if (!Number.isFinite(this.ceilingLimit) || dt === 0) this.ceilingLimit = targetLimit;
    const smoothTime = targetLimit < this.ceilingLimit ? c.overheadSmoothTime : c.overheadReleaseTime;
    this.ceilingLimit = smoothDamp(this.ceilingLimit, targetLimit, this.ceilingSpring, smoothTime, dt);
    let cameraY = frame.y + softMin(desiredY, this.ceilingLimit, OVERHEAD_BLEND);
    // Raised ground along the camera path (container roofs): the low slide view on a roof would dip into
    // the container – the camera rises over it in time, like the overhead glide in reverse.
    const ground = input.groundAt?.(cameraDistance - 0.5, cameraDistance + c.overheadLookAhead, this.lateral + a.offsetX) ?? null;
    const targetFloor = ground === null ? -Infinity : ground + c.groundClearance;
    if (!Number.isFinite(this.floorLimit) || !Number.isFinite(targetFloor) || dt === 0) {
      this.floorLimit = targetFloor;
      this.floorSpring.velocity = 0;
    } else {
      const floorTime = targetFloor > this.floorLimit ? c.overheadSmoothTime : c.overheadReleaseTime;
      this.floorLimit = smoothDamp(this.floorLimit, targetFloor, this.floorSpring, floorTime, dt);
    }
    cameraY = Math.max(cameraY, frame.y + this.floorLimit);
    // Safety net directly at the camera position (only bites if the glide could not keep up).
    const above = input.ceilingAt?.(cameraDistance - 0.3, cameraDistance + 0.3, this.lateral + a.offsetX) ?? null;
    if (above !== null) cameraY = Math.min(cameraY, frame.y + above - c.nearPlane - 0.05);

    this.camera.position.set(px - fwd.x * back + right.x * a.offsetX, cameraY, pz - fwd.z * back + right.z * a.offsetX);
    const lookAhead = c.lookAhead * (1 - focus);
    this.target.set(
      px + fwd.x * lookAhead + right.x * a.offsetX,
      // Focused on the runner the camera may drop, the look-at point stays on the runner (no look at the ground).
      frame.y + this.height + c.lookHeight + a.offsetY * (1 - focus) + a.targetOffsetY,
      pz + fwd.z * lookAhead + right.z * a.offsetX,
    );
    this.camera.setTarget(this.target);
    this.applyRoll(a.rollDegrees * DEG_TO_RAD);
    this.camera.fov = this.effectiveFov(a.fovDegrees * DEG_TO_RAD);
  }

  /**
   * Roll via an explicit up vector around the view direction – rebuilt every
   * frame from world up. Never use camera.rotation.z: when it changes, Babylon
   * bakes the current pitch/yaw into camera.upVector, and after the next 90°
   * turn that stale up vector tilts the horizon (until the roll changes again).
   */
  private applyRoll(roll: number): void {
    this.viewDirection.copyFrom(this.target).subtractInPlace(this.camera.position).normalize();
    Quaternion.RotationAxisToRef(this.viewDirection, roll, this.rollRotation);
    Vector3.UpReadOnly.rotateByQuaternionToRef(this.rollRotation, this.camera.upVector);
  }

  /** Portrait phone screen (CameraConfig.portrait): closer camera, narrower minimum FOV. */
  private isPortrait(): boolean {
    return this.camera.getEngine().getAspectRatio(this.camera) < this.config.portrait.maxAspect;
  }

  /** Widens the vertical FOV on narrow (portrait) screens to keep the minimum horizontal FOV. */
  private effectiveFov(verticalFov: number): number {
    const aspect = this.camera.getEngine().getAspectRatio(this.camera);
    const minDegrees = this.isPortrait() ? this.config.portrait.minHorizontalDegrees : this.config.fov.minHorizontalDegrees;
    const minHorizontal = minDegrees * DEG_TO_RAD;
    const verticalForMinHorizontal = 2 * Math.atan(Math.tan(minHorizontal / 2) / Math.max(aspect, 0.01));
    return Math.max(verticalFov, verticalForMinHorizontal);
  }

  /** Called when the player stops abruptly (hit) – the camera eases in. */
  addForwardLag(meters: number): void {
    this.lag += meters;
  }
}
